import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import WaitlistForm from "@/components/WaitlistForm";

gsap.registerPlugin(ScrollTrigger);

const VIDEO_SRC = "/evove.mp4";
const POSTER = "/poster.jpg";
const FINAL = "/final.jpg";

const HEADLINE = ["Triple-Target Serum For Skin Longevity"];

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Overlay({ scrolled }) {
  return (
    <div className="absolute inset-0 z-10 px-6 md:px-16 py-7 md:py-9 pointer-events-none">
      {/* left content column */}
      <div
        className="absolute left-6 md:left-16 top-[48%] md:top-1/2 md:-translate-y-1/2 w-[86%] sm:w-auto sm:max-w-[520px] lg:max-w-[46%] pointer-events-auto"
        data-testid="hero-headline"
      >
        <h1 className="headline text-[1.55rem] leading-[1.1] sm:text-5xl lg:text-6xl">
          {HEADLINE.map((line, i) => (
            <span className="mask-line" key={line}>
              <motion.span
                style={{ display: "block" }}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1.05,
                  delay: 0.35 + i * 0.11,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="mt-3 md:mt-4 max-w-[22rem] text-[0.82rem] md:text-[0.95rem] leading-relaxed"
          style={{ color: "var(--paper-dim)" }}
          data-testid="hero-subline"
        >
          Join the waitlist for early access &amp; exclusive community offers.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 md:mt-8"
        >
          <WaitlistForm />
        </motion.div>
      </div>

      {/* scroll cue */}
      <div
        className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 transition-opacity duration-700"
        style={{ opacity: scrolled ? 0 : 1, color: "var(--paper-dim)" }}
      >
        <span className="text-[0.58rem] track-sm uppercase [writing-mode:vertical-rl]">
          Scroll to reveal
        </span>
      </div>
    </div>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (prefersReduced) return;

    const video = videoRef.current;
    const section = sectionRef.current;
    const pin = pinRef.current;
    if (!video || !section || !pin) return;
    video.muted = true;

    const lenis = new Lenis({ lerp: 0.06, smoothWheel: true });
    window.lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    let targetTime = 0;
    let isSeeking = false;
    const EPS = 1 / 48;

    const reveal = () => setReady(true);

    const doSeek = () => {
      if (isSeeking || video.readyState < 1) return;
      if (Math.abs(video.currentTime - targetTime) < EPS) return;
      isSeeking = true;
      try {
        video.currentTime = targetTime;
      } catch (_) {
        isSeeking = false;
      }
    };
    const onSeeked = () => {
      isSeeking = false;
      reveal();
      doSeek();
    };
    const onLoadedData = () => {
      reveal();
      doSeek();
    };
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("loadeddata", onLoadedData);
    video.addEventListener("canplaythrough", reveal);

    const supportsRVFC = typeof video.requestVideoFrameCallback === "function";
    let rafId = requestAnimationFrame(function beat() {
      doSeek();
      rafId = requestAnimationFrame(beat);
    });
    const onFrame = () => {
      doSeek();
      if (supportsRVFC) video.requestVideoFrameCallback(onFrame);
    };
    if (supportsRVFC) video.requestVideoFrameCallback(onFrame);

    const st = ScrollTrigger.create({
      trigger: section,
      start: "top top",
      end: "bottom bottom",
      pin: pin,
      pinSpacing: true,
      scrub: 1.1,
      onUpdate: (self) => {
        const d = video.duration || 5;
        targetTime = self.progress * d;
        doSeek();
        if (progressRef.current)
          gsap.set(progressRef.current, { scaleY: self.progress });
        setScrolled(self.progress > 0.01);
      },
    });

    video.load();
    doSeek();

    const onWinLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onWinLoad);
    const refreshT = setTimeout(() => ScrollTrigger.refresh(), 600);

    return () => {
      st.kill();
      gsap.ticker.remove(tick);
      lenis.destroy();
      window.lenis = null;
      cancelAnimationFrame(rafId);
      clearTimeout(refreshT);
      window.removeEventListener("load", onWinLoad);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("loadeddata", onLoadedData);
      video.removeEventListener("canplaythrough", reveal);
    };
  }, []);

  if (prefersReduced) {
    return (
      <div className="stage-bg relative min-h-screen w-full overflow-hidden" data-testid="hero-root">
        <img
          src={FINAL}
          alt="Evove Skin Longevity Triple Serum bottle"
          fetchpriority="high"
          decoding="async"
          className="scrub-video absolute top-[60px] left-0 w-full h-[38%] object-cover object-center scale-[1.15] origin-center lg:top-0 lg:scale-100 lg:left-auto lg:right-0 lg:h-full lg:w-[56%]"
        />
        <Overlay scrolled />
      </div>
    );
  }

  return (
    <div className="stage-bg" data-testid="hero-root">
      <section
        ref={sectionRef}
        style={{ height: "250vh" }}
        data-testid="scrub-section"
      >
        <div
          ref={pinRef}
          className="relative h-screen w-full overflow-hidden stage-bg"
        >
          <video
            ref={videoRef}
            className="scrub-video absolute top-[60px] left-0 w-full h-[38%] object-cover object-center scale-[1.15] origin-center lg:top-0 lg:scale-100 lg:left-auto lg:right-0 lg:h-full lg:w-[56%]"
            src={VIDEO_SRC}
            poster={POSTER}
            muted
            playsInline
            preload="auto"
            data-testid="scrub-video"
          />
          <img
            src={POSTER}
            alt=""
            aria-hidden="true"
            fetchpriority="high"
            decoding="async"
            className="scrub-video absolute top-[60px] left-0 w-full h-[38%] object-cover object-center scale-[1.15] origin-center lg:top-0 lg:scale-100 lg:left-auto lg:right-0 lg:h-full lg:w-[56%] transition-opacity duration-500"
            style={{ opacity: ready ? 0 : 1 }}
            data-testid="hero-poster"
          />
          <div className="absolute right-[10px] top-0 h-full w-px bg-[rgba(27,23,53,0.12)] z-20 hidden md:block">
            <div
              ref={progressRef}
              className="w-full origin-top"
              style={{
                height: "100%",
                transform: "scaleY(0)",
                background: "var(--paper)",
              }}
            />
          </div>
          <Overlay scrolled={scrolled} />
        </div>
      </section>
    </div>
  );
}
